import { Category, MessageType } from '../types';

/** How Quu Dashboard / Newsflow cards become HotQuus for a station (#40, #41). */
export type NewsflowPublishMode = 'manual' | 'automatic';

/** Display During for Newsflow: no Music option (#41). */
export type NewsflowDisplayDuring = 'other' | 'ads' | 'other_ads';

export type NewsflowRotation = 'takeover' | 'override' | 'rotate_with';

export interface NewsflowStationSettings {
  publishMode: NewsflowPublishMode;
  categoryId: string;
  categoryName: string;
  rotation: NewsflowRotation;
  keepAliveMinutes: number;
  /** Default 30s (#40). */
  durationSeconds: number;
  displayDuring: NewsflowDisplayDuring;
}

export const DEFAULT_NEWSFLOW_DURATION_SECONDS = 30;

export function defaultNewsflowStationSettings(
  categories: Category[] = []
): NewsflowStationSettings {
  const first = categories[0];
  return {
    publishMode: 'manual',
    categoryId: first?.id ?? 'Misc',
    categoryName: first?.name ?? 'Misc',
    rotation: 'rotate_with',
    keepAliveMinutes: 90,
    durationSeconds: DEFAULT_NEWSFLOW_DURATION_SECONDS,
    displayDuring: 'other_ads',
  };
}

export function newsflowRotationToMessageType(rotation: NewsflowRotation): MessageType {
  if (rotation === 'takeover') return MessageType.TAKEOVER;
  if (rotation === 'override') return MessageType.OVERRIDE;
  return MessageType.ROTATE_WITH;
}

/** Quu API type: 0 takeover, 1 rotate, 2 override */
export function newsflowRotationToApiType(rotation: NewsflowRotation): string {
  if (rotation === 'takeover') return '0';
  if (rotation === 'rotate_with') return '1';
  return '2';
}

/** option_ids: 2 Other, 3 Ads (no Music for Newsflow). */
export function newsflowDisplayDuringToOptions(during: NewsflowDisplayDuring): string[] {
  if (during === 'other') return ['2'];
  if (during === 'ads') return ['3'];
  return ['2', '3'];
}

const STORAGE_PREFIX = 'newsflowStationSettings:';

export function loadNewsflowStationSettings(
  stationId: string,
  categories: Category[] = []
): NewsflowStationSettings {
  const defaults = defaultNewsflowStationSettings(categories);
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${stationId}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<NewsflowStationSettings>;
    return {
      ...defaults,
      ...parsed,
      durationSeconds: Number(parsed.durationSeconds) || DEFAULT_NEWSFLOW_DURATION_SECONDS,
      publishMode: parsed.publishMode === 'automatic' ? 'automatic' : 'manual',
    };
  } catch {
    return defaults;
  }
}

export function saveNewsflowStationSettings(
  stationId: string,
  settings: NewsflowStationSettings
): void {
  localStorage.setItem(`${STORAGE_PREFIX}${stationId}`, JSON.stringify(settings));
}

/** Payload shape when Quu Dashboard sends a card via "Send to Quu". */
export interface NewsflowCardPayload {
  name?: string;
  title?: string;
  line1?: string;
  line2?: string;
  rt1?: string;
  rt2?: string;
  imageUrl?: string | null;
  stationId: string | number;
}

/**
 * Build saveHotKey fields from a Newsflow card using station-level defaults (#40 / #41).
 * activate: 0 = manual queue (Ready), 1 = automatic publish on air.
 */
export function buildHotKeyPayloadFromNewsflow(
  card: NewsflowCardPayload,
  settings: NewsflowStationSettings
): {
  name: string;
  category: string;
  categoryId?: string;
  minutesAlive: number;
  rt1: string;
  rt2: string;
  imageUrl: string | null;
  radio_station_ids: number[];
  type: string;
  durationInSeconds: number;
  options: string[];
  activate: 0 | 1;
} {
  const line1 = (card.rt1 ?? card.line1 ?? card.title ?? '').trim();
  const line2 = (card.rt2 ?? card.line2 ?? '').trim();
  const name = String(card.name ?? card.title ?? (line1 || 'Newsflow Quu')).trim();
  const stationId = Number(card.stationId);

  return {
    name,
    category: settings.categoryName || settings.categoryId || 'Misc',
    categoryId: settings.categoryId,
    minutesAlive: Math.max(0, Math.floor(settings.keepAliveMinutes)) || 90,
    rt1: line1,
    rt2: line2,
    imageUrl: card.imageUrl ?? null,
    radio_station_ids: Number.isFinite(stationId) ? [stationId] : [],
    type: newsflowRotationToApiType(settings.rotation),
    durationInSeconds: settings.durationSeconds || DEFAULT_NEWSFLOW_DURATION_SECONDS,
    options:
      settings.rotation === 'takeover'
        ? ['1', '2', '3']
        : newsflowDisplayDuringToOptions(settings.displayDuring),
    activate: settings.publishMode === 'automatic' ? 1 : 0,
  };
}
